/**
 * Studio AI Page
 * Main router: Landing → Wizard → StyleTemplateCreator
 */

import { useCallback, useEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { GraphFileItem } from "@/api/models"

import { SidebarInset } from "@/components/ui/sidebar"
import { FilePickerDialog } from "@/features/chat/components/file-picker-dialog"
import { ImageEditorView } from "@/features/studio-ai/components/image-editor/image-editor-view"
import {
  graphItemToImageFile,
  isSupportedStudioAiImage,
  STUDIO_AI_IMAGE_ACCEPT,
  STUDIO_AI_IMAGE_MAX_SIZE_BYTES,
} from "@/features/studio-ai/components/image-picker-utils"
import { StudioAILanding } from "@/features/studio-ai/components/landing/studio-ai-landing"
import { StudioAIWelcomeModal } from "@/features/studio-ai/components/studio-ai-welcome-modal"
import { ImageWizard } from "@/features/studio-ai/components/wizard/image-wizard"
import { VideoWizard } from "@/features/studio-ai/components/wizard/video-wizard"
import { useGenerationMonitor } from "@/features/studio-ai/hooks/use-generation-monitor"
import { useUserJobs } from "@/features/studio-ai/hooks/use-user-jobs"
import { MediaType } from "@/features/studio-ai/types/studio-ai.types"
import {
  closeStyleTemplateInSidebar,
  openStyleTemplateInSidebar,
} from "@/features/studio-ai/utils/style-template-sidebar"
import { fileToDataUrl } from "@/lib/file-utils"
import { useStudioAIStore } from "@/stores/studio-ai-store"

export function StudioAIPage() {
  const { t } = useTranslation(undefined, { keyPrefix: "studioAi" })
  const [showWelcomeModal, setShowWelcomeModal] = useState(false)

  const currentView = useStudioAIStore((state) => state.currentView)
  const setCurrentView = useStudioAIStore((state) => state.setCurrentView)
  const mediaType = useStudioAIStore((state) => state.mediaType)
  const setMediaType = useStudioAIStore((state) => state.setMediaType)
  const setEditorImageUrl = useStudioAIStore((state) => state.setEditorImageUrl)
  const generationState = useStudioAIStore((state) => state.generationState)
  const resetGenerationState = useStudioAIStore((state) => state.resetGenerationState)
  const resetWizard = useStudioAIStore((state) => state.resetWizard)
  const jobs = useStudioAIStore((state) => state.jobs)
  const [isFilePickerOpen, setIsFilePickerOpen] = useState(false)

  const { isLoading: isLoadingJobs, refetch: refetchJobs } = useUserJobs()

  // Clean up stale generation state on mount
  useEffect(() => {
    if (generationState.isGenerating && !generationState.currentJobId) {
      resetGenerationState()
    }
  }, [])

  useEffect(() => {
    return () => {
      closeStyleTemplateInSidebar()
    }
  }, [])

  useEffect(() => {
    if (currentView !== "style-template-creator") return
    setCurrentView("landing")
    openStyleTemplateInSidebar()
  }, [currentView, setCurrentView])

  // Refetch jobs when any view transitions back to landing
  const prevViewRef = useRef(currentView)

  useEffect(() => {
    const prevView = prevViewRef.current
    prevViewRef.current = currentView
    if (
      currentView === "landing" &&
      prevView !== "landing" &&
      prevView !== "style-template-creator"
    ) {
      void refetchJobs()
    }
  }, [currentView, refetchJobs])

  // Real-time status updates (WebSocket primary, HTTP polling fallback)
  useGenerationMonitor({
    jobId: generationState.currentJobId,
    enabled: !!generationState.currentJobId && generationState.isGenerating,
    mediaType: mediaType === MediaType.Image ? "image" : "video",
    onComplete: () => {
      toast.success(t("gallery.generationCompleted"), {
        description: t("gallery.generationCompletedDesc"),
      })
      resetGenerationState()
      void refetchJobs()
    },
    onError: (error) => {
      toast.error(t("gallery.generationFailed"), {
        description: error,
      })
      resetGenerationState()
    },
  })

  const handleCreateImages = () => {
    setMediaType(MediaType.Image)
    setCurrentView("image-wizard")
  }

  const handleCreateVideos = () => {
    setMediaType(MediaType.Video)
    setCurrentView("video-wizard")
  }

  const handleEditImages = () => {
    setIsFilePickerOpen(true)
  }

  const handleCreateStyle = () => {
    openStyleTemplateInSidebar()
  }

  const handleLocalFilesSelected = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return
      const file = files[0]

      if (!isSupportedStudioAiImage(file)) {
        toast.error(t("imageEdit.invalidFileType"))
        return
      }
      if (file.size > STUDIO_AI_IMAGE_MAX_SIZE_BYTES) {
        toast.error(t("imageEdit.fileTooLarge"))
        return
      }

      const dataUrl = await fileToDataUrl(file)
      setEditorImageUrl(dataUrl)
      setCurrentView("image-editor")
    },
    [setEditorImageUrl, setCurrentView, t]
  )

  const handleCloudFileSelected = useCallback(
    async (item: GraphFileItem) => {
      const file = await graphItemToImageFile(item)

      if (file.size > STUDIO_AI_IMAGE_MAX_SIZE_BYTES) {
        toast.error(t("imageEdit.fileTooLarge"))
        return
      }

      const dataUrl = await fileToDataUrl(file)
      setEditorImageUrl(dataUrl)
      setCurrentView("image-editor")
    },
    [setEditorImageUrl, setCurrentView, t]
  )

  const handleWizardComplete = () => {
    resetWizard() // sets currentView to "landing", which triggers the useEffect refetch
  }

  const handleWizardCancel = () => {
    resetWizard()
  }

  return (
    <SidebarInset className="module-enter min-h-[calc(100svh-64px)] overflow-x-hidden">
      {currentView === "image-editor" ? (
        <ImageEditorView />
      ) : (
        <div className="container mx-auto max-w-7xl p-4 sm:p-6">
          {currentView === "landing" && (
            <StudioAILanding
              isLoadingJobs={isLoadingJobs}
              jobs={jobs}
              onCreateImages={handleCreateImages}
              onCreateStyle={handleCreateStyle}
              onCreateVideos={handleCreateVideos}
              onEditImages={handleEditImages}
              onOpenWelcome={() => setShowWelcomeModal(true)}
              onRefreshJobs={() => void refetchJobs()}
            />
          )}

          {currentView === "image-wizard" && (
            <ImageWizard onCancel={handleWizardCancel} onComplete={handleWizardComplete} />
          )}

          {currentView === "video-wizard" && (
            <VideoWizard onCancel={handleWizardCancel} onComplete={handleWizardComplete} />
          )}

          <StudioAIWelcomeModal open={showWelcomeModal} onOpenChange={setShowWelcomeModal} />
        </div>
      )}

      <FilePickerDialog
        accept={STUDIO_AI_IMAGE_ACCEPT}
        allowedSources={["recent", "local", "oneDrive", "sharePoint", "sharedWithMe"]}
        open={isFilePickerOpen}
        selectionMode="single"
        source="local"
        onLocalFilesSelected={handleLocalFilesSelected}
        onOpenChange={setIsFilePickerOpen}
        onSelect={handleCloudFileSelected}
      />
    </SidebarInset>
  )
}
